import { Badge, Button, PlatformMark, Skeleton } from '../../ui';
import { formatUTCDate } from '../../../utils/dates';
import { AccountPicker } from './AccountPicker';

/**
 * Says whose pulse you are reading.
 *
 * The page used to name the account nowhere at all: you could read 728 replies
 * about a person and never learn who they were. The design assumed you had
 * chosen the account from the picker seconds earlier, which is true from the
 * picker and false from every deep link - including the Heartbeat button on
 * AccountCard, the main way in.
 *
 * The switcher lives inside this header rather than as two empty selects above
 * the content, so the page leads with identity instead of with a form.
 */
export function AccountIdentity({ account, isLoading, currentAccountId, lastPulseAt, onContext }) {
	if (isLoading) {
		return (
			<div className="mb-6 flex items-center gap-4 rounded-xl border border-border bg-card p-4 shadow-card">
				<Skeleton className="h-12 w-12 rounded-full" />
				<div className="flex-1 space-y-2">
					<Skeleton className="h-4 w-40" />
					<Skeleton className="h-3 w-24" />
				</div>
			</div>
		);
	}

	if (!account) return <div className="mb-6"><AccountPicker currentAccountId={currentAccountId} account={account} /></div>;

	const clientName = account.client?.name;
	const followers = typeof account.followerCount === 'number'
		? account.followerCount.toLocaleString()
		: null;
	// formatUTCDate returns null for a missing or unparseable value, so format
	// first and branch on the result - branching on the raw prop would render
	// "last pulse null" for a date that exists but cannot be parsed.
	const lastPulse = formatUTCDate(lastPulseAt);

	return (
		<div className="mb-6 rounded-xl border border-border bg-card p-4 shadow-card">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex min-w-0 items-center gap-3">
					<div className="relative shrink-0">
						<img
							src={account.avatar || 'https://placehold.co/48x48'}
							alt={account.handle}
							className="h-12 w-12 rounded-full bg-muted object-cover"
						/>
						<PlatformMark
							platform={account.platform}
							size="xs"
							className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white"
						/>
					</div>
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h2 className="truncate text-lg font-semibold text-foreground">
								@{account.handle}
							</h2>
							{account.isVerified && <Badge variant="outline" size="sm">Verified</Badge>}
							{onContext && <Button variant="outline" size="sm" onClick={onContext}>Context</Button>}
						</div>
						<p className="truncate text-sm text-muted-foreground">
							{[
								account.displayName || null,
								clientName,
								followers ? `${followers} followers` : null,
								lastPulse ? `last pulse ${lastPulse}` : 'no pulse yet',
							].filter(Boolean).join(' · ')}
						</p>
					</div>
				</div>

				<div className="sm:max-w-md sm:flex-1">
					<AccountPicker currentAccountId={currentAccountId} account={account} compact />
				</div>
			</div>
		</div>
	);
}
